/**
 * Domain models for the signed-in user.
 *
 * `CurrentUser` is the auth wrapper persisted under the `userDetails`
 * web-storage key (see `AuthService.saveUserDetail`); the actual person is
 * its `user_details`. Related shapes: `MasqueradeUserResponse`
 * (masquerade.model.ts) and `SubscriptionPlan.user_details`
 * (stripe.models.ts) describe the same person as returned by other
 * endpoints.
 */

/**
 * Per-user feature entitlement map from `GET v2/meta-data`
 * (`result.user_features`). Every active catalog key is always present;
 * a key absent from the map (or the map itself being absent/undefined)
 * means "not entitled" — callers must treat that as `false`, never as an
 * error. See {@link userHasNotificationCenterAccess} for the first
 * consumer (issue #559).
 */
export type UserFeatureMap = Record<string, boolean>;

export interface UserCompany {
  id: string;
  name: string;
  slug: string;
  instancy_id: string;
  created_at: string;
  updated_at: string;
  /**
   * FlyMSG Roleplay add-on visibility for this company. The ONLY value that
   * matters is `"hidden"` — the company has turned the add-on off. Anything
   * else (including the field being absent) leaves Roleplay on.
   * See {@link userHasRoleplayAccess}.
   */
  roleplay_addon_access?: string;
}

export interface UserDetails {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  avatar: string;
  updated_at: string;
  created_at: string;
  email_verified_at: string;
  last_login: string;
  hubspot_id: string;
  heap_analytics_id?: string;
  onboardingv2_presented: boolean;
  onboardingv2_completed?: boolean;
  onboardingv2_step: number;
  onboarding_percentage: number;
  onboarding_step: string;
  onboarding: string;
  /**
   * flymsg-be#759 (in parallel development alongside FE issue #523): the
   * per-step completion set behind the new "Onboarding" checklist percentage
   * (see OnboardingProgressService / OnboardingStepKey). Optional because
   * older backend deploys don't send it yet — callers MUST tolerate its
   * absence rather than assume the field exists.
   */
  onboarding_steps_completed?: string[];
  /** flymsg-be#759: set once the user reaches 100% of the tracked steps. */
  last_onboarding_completed_at?: string;
  referral_key: string;
  referrals_count: number;
  rewardable: number;
  rewards_level: number;
  average_charactors_count: number;
  recently_used_get_updated: boolean;
  team_id: string[];
  company_group_id: string;
  activation_date: string;
  company_id: string;
  company_name?: string;
  status: string;
  instancy_id: string;
  /**
   * Beta cohort flag. Selects the testing browser-extension id
   * (`ExtensionService`); it no longer gates Roleplay — see
   * {@link userHasRoleplayAccess}.
   */
  is_beta: boolean;
  is_poc?: boolean;
  /**
   * Comma-separated role list. Masquerade responses may deliver it as a
   * string[] instead (see MasqueradeUserResponse) — normalize via
   * `AuthService.getUserRole()`.
   */
  role: string | string[];
  company: UserCompany;
  /** Present only during the temp-password login flow. */
  temp_password?: string;
  tooltip_learning_resources_showed?: boolean;
  /** Company-invite flow fields (stripped before persisting — see AuthService). */
  invited_to_company?: boolean;
  invited_to_company_by_admin?: string;
  /**
   * Per-user feature entitlement map (issue #559), merged onto the cached
   * user record by `AuthService.refreshUserFeatures()` from `GET
   * v2/meta-data`. Optional because it isn't part of the login response
   * itself and a stale cached record (older session, or the refresh
   * fetch failing/racing) may not have it yet — treat its absence, or the
   * absence of any individual key on it, as `false`.
   */
  user_features?: UserFeatureMap;
}

/**
 * Whether this user may access FlyMSG Roleplay.
 *
 * Roleplay is GENERALLY AVAILABLE: every signed-in user passes. The single
 * exception is a corporate account whose company has switched the add-on off
 * (`roleplay_addon_access === "hidden"`) — that, and only that, denies access.
 * A company that has never set the field is NOT disabled, so it passes; so do
 * individual users, who have no company to disable it.
 *
 * (Previously this was a beta gate: company users needed the company setting
 * explicitly non-"hidden", and individual users needed the `is_beta` cohort
 * flag. Both of those denied by default — the opposite of the rule above.)
 *
 * A plain function (not a service method) so `AuthService` can use it too —
 * `SsoRedirectService` injects `AuthService`, so the gate living only there
 * would create a circular dependency.
 */
export function userHasRoleplayAccess(user: UserDetails | null | undefined): boolean {
  if (!user) {
    return false;
  }
  return user.company?.roleplay_addon_access !== "hidden";
}

/**
 * Whether this user may see the in-app notification center (issue #416,
 * updated for #559).
 *
 * MANDATORY PRODUCT GATE: the whole feature — bell icon, unread badge,
 * panel, the Socket.IO connection, the REST fetch and the unread-count
 * polling fallback — is gated on the `in_app_notifications` key of the
 * per-user feature map (`UserDetails.user_features`, sourced from `GET
 * v2/meta-data`'s `result.user_features`), NOT on the `is_beta` cohort
 * flag. A missing map, or a missing/false key on it, means "not
 * entitled" — treat that as `false`, never as an error. This is the
 * single source of truth for that gate; every entry point
 * (`HeaderComponent`, `AppNotificationService.init()`) calls this instead
 * of re-reading the feature map locally, so the rollout can't drift
 * between the UI gate and the network gate the way
 * `userHasRoleplayAccess`'s docstring warns about for its own history.
 * The backend independently enforces its own delivery gate on the same
 * key — this client-side check is belt-and-braces, not the only guard.
 */
export function userHasNotificationCenterAccess(user: UserDetails | null | undefined): boolean {
  return user?.user_features?.in_app_notifications === true;
}

/**
 * Whether this user is entitled to FlyMSG Sentence Rewriter (issue #561).
 *
 * MANDATORY PRODUCT GATE, same shape as {@link userHasNotificationCenterAccess}:
 * the Sentence Rewriter card and its column in the Disabled Sites manager
 * render only for users entitled via the per-user feature map's
 * `sentence_rewriter` key (`UserDetails.user_features`, sourced from `GET
 * v2/meta-data`'s `result.user_features`) — NOT the `is_beta` cohort flag. A
 * missing map, or a missing/false key on it, means "not entitled" — treat
 * that as `false`, never as an error. This is the single source of truth for
 * that gate; every entry point calls this instead of re-reading the feature
 * map locally, so the rollout can't drift between the UI gate and the
 * network gate the way `userHasRoleplayAccess`'s docstring warns about for
 * its own history.
 */
export function userHasSentenceRewriterAccess(user: UserDetails | null | undefined): boolean {
  return user?.user_features?.sentence_rewriter === true;
}

/**
 * The auth wrapper stored under the `userDetails` key and exposed by
 * `AuthService.userDetails`. Note: `company` here is the company SLUG
 * (string), while `user_details.company` is the company object.
 */
export interface CurrentUser {
  /** Mirror of user_details.id present on some auth payloads. */
  id?: string;
  access_token: string;
  /** Absent during masquerade sessions. */
  refresh_token?: string;
  user_details: UserDetails;
  /** Company slug (NOT the company object — that lives on user_details.company). */
  company?: string;
  provider?: string;
  is_company_poc?: boolean;
  is_password_set?: boolean;
}
